import type { Size } from '@/components/pdf-viewer';
import type { Annotation } from './annotation';

// Annotations keep the stored/API shape { x, y, width, height, containerRect }, but
// only the ratios matter: position / containerRect is the position as a fraction of
// the page. New annotations are written in 96-dpi base px of the page with
// containerRect = the page's base size; components stored by older versions hold
// browser px with containerRect = the rendered page rect. Both read the same here.

export type PageRect = { x: number; y: number; width: number; height: number };

type AnnotationBox = Pick<Annotation, 'x' | 'y' | 'width' | 'height'> & {
	containerRect: Size;
};

/** Position as fractions (0..1) of the page, top-left origin. */
export function annotationToPageFractions(annotation: AnnotationBox): PageRect {
	const { width, height } = annotation.containerRect;
	return {
		x: annotation.x / width,
		y: annotation.y / height,
		width: annotation.width / width,
		height: annotation.height / height,
	};
}

/** Scales page fractions to a page of `size` (base px, PDF points, ...). */
export const scalePageFractions = (
	fractions: PageRect,
	size: Size,
): PageRect => ({
	x: fractions.x * size.width,
	y: fractions.y * size.height,
	width: fractions.width * size.width,
	height: fractions.height * size.height,
});

/** Where to draw the annotation on a page laid out at `pageSize` base px. */
export const annotationToPageBox = (
	annotation: AnnotationBox,
	pageSize: Size,
) => scalePageFractions(annotationToPageFractions(annotation), pageSize);

/**
 * Re-expresses an annotation in base px of a page of `pageSize`, the format new
 * annotations are stored in.
 */
export function annotationInPageBasePx<T extends AnnotationBox>(
	annotation: T,
	pageSize: Size,
): T {
	return {
		...annotation,
		...annotationToPageBox(annotation, pageSize),
		containerRect: { width: pageSize.width, height: pageSize.height },
	};
}

/**
 * Signing-request components come back from the API with numeric fields as
 * strings; coerce them so the geometry above can use them.
 */
export function normalizeStoredAnnotation(component: Annotation): Annotation {
	return {
		...component,
		page: +component.page,
		x: +component.x,
		y: +component.y,
		width: +component.width,
		height: +component.height,
		containerRect: {
			...component.containerRect,
			width: +component.containerRect.width,
			height: +component.containerRect.height,
		},
	};
}

/**
 * Rect in PDF points with a TOP-DOWN y (0 = top edge of the page), the convention
 * the signer backend's visible-signature options use.
 */
export const annotationToPdfPointsTopDown = (
	annotation: AnnotationBox,
	pageSizePt: Size,
): PageRect => annotationToPageBox(annotation, pageSizePt);

/**
 * Rect in PDF points with PDF's native BOTTOM-UP y (0 = bottom edge), as pdf-lib's
 * drawImage / addToPage expect: y is the rect's bottom edge.
 */
export function annotationToPdfPointsBottomUp(
	annotation: AnnotationBox,
	pageSizePt: Size,
): PageRect {
	const rect = annotationToPageBox(annotation, pageSizePt);
	return { ...rect, y: pageSizePt.height - rect.y - rect.height };
}
